import * as React from 'react';
import { createPublicUploadURL, getFileStatus } from '@seaters/data-access';
import { $getSelection, $isRangeSelection, $setSelection } from 'lexical';
import type { InputRef } from 'antd';
import type { LexicalEditor, RangeSelection } from 'lexical';
import type { ToolbarImageController } from './types';
import { INSERT_IMAGE_COMMAND } from '../ImagesPlugin';

export function useToolbarImage(editor: LexicalEditor): ToolbarImageController {
  const [imageAspectRatio, setImageAspectRatio] = React.useState<number | null>(
    null
  );
  const [heightValue, setHeightValue] = React.useState('');
  const [isEditorVisible, setIsEditorVisible] = React.useState(false);
  const [isUrlLocked, setIsUrlLocked] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [urlValue, setUrlValue] = React.useState('');
  const [isUploading, setIsUploading] = React.useState(false);
  const [widthValue, setWidthValue] = React.useState('');
  const savedSelectionRef = React.useRef<RangeSelection | null>(null);
  const inputRef = React.useRef<InputRef>(null);
  const fileInputRef = React.useRef<React.ComponentRef<'input'>>(null);

  React.useEffect(() => {
    if (!isEditorVisible) {
      return;
    }

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditorVisible]);

  React.useEffect(() => {
    const normalizedUrl = urlValue.trim();

    if (!isEditorVisible || normalizedUrl === '') {
      return;
    }

    let isCancelled = false;
    const image = new globalThis.Image();

    image.onload = () => {
      if (isCancelled || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
        return;
      }

      setImageAspectRatio(image.naturalWidth / image.naturalHeight);
      setWidthValue(String(Math.round(image.naturalWidth)));
      setHeightValue(String(Math.round(image.naturalHeight)));
    };

    image.src = normalizedUrl;

    return () => {
      isCancelled = true;
    };
  }, [isEditorVisible, urlValue]);

  const closeEditor = React.useCallback(() => {
    setIsEditorVisible(false);
    setImageAspectRatio(null);
    setIsUrlLocked(false);
    setUploadError('');
    setUploadProgress(0);
    setIsUploading(false);
    savedSelectionRef.current = null;
    setUrlValue('');
    setWidthValue('');
    setHeightValue('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const openEditor = React.useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        savedSelectionRef.current = selection.clone();
      } else {
        savedSelectionRef.current = null;
      }
    });

    setIsEditorVisible(true);
  }, [editor]);

  const updateImageDimensionsFromSource = React.useCallback((src: string) => {
    if (!src) {
      return;
    }

    const image = new globalThis.Image();

    image.onload = () => {
      if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
        return;
      }

      setImageAspectRatio(image.naturalWidth / image.naturalHeight);
      setWidthValue(String(Math.round(image.naturalWidth)));
      setHeightValue(String(Math.round(image.naturalHeight)));
    };

    image.src = src;
  }, []);

  const handleWidthChange = React.useCallback(
    (nextValue: string) => {
      setWidthValue(nextValue);

      const parsedWidth = Number(nextValue.trim());

      if (imageAspectRatio && Number.isFinite(parsedWidth) && parsedWidth > 0) {
        setHeightValue(String(Math.round(parsedWidth / imageAspectRatio)));
      }
    },
    [imageAspectRatio]
  );

  const handleHeightChange = React.useCallback(
    (nextValue: string) => {
      setHeightValue(nextValue);

      const parsedHeight = Number(nextValue.trim());

      if (
        imageAspectRatio &&
        Number.isFinite(parsedHeight) &&
        parsedHeight > 0
      ) {
        setWidthValue(String(Math.round(parsedHeight * imageAspectRatio)));
      }
    },
    [imageAspectRatio]
  );

  const uploadImageFile = React.useCallback(
    async (file: InstanceType<typeof globalThis.File>) => {
      setUploadError('');
      setUploadProgress(0);
      setIsUploading(true);

      try {
        const localPreviewUrl = globalThis.URL?.createObjectURL(file);

        if (localPreviewUrl) {
          updateImageDimensionsFromSource(localPreviewUrl);
        }

        const { url, key } = await createPublicUploadURL(
          file.type as Parameters<typeof createPublicUploadURL>[0]
        );

        await new Promise<void>((resolve, reject) => {
          const xhr = new globalThis.XMLHttpRequest();

          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              setUploadProgress(Math.round((event.loaded / event.total) * 100));
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status === 200) {
              resolve();
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          });

          xhr.addEventListener('error', () => {
            reject(new Error('Upload failed'));
          });

          xhr.open('PUT', url);
          xhr.send(file);
        });

        const statusResponse = await getFileStatus(key);

        if (!statusResponse.url) {
          throw new Error('File processing completed but no URL available');
        }

        setUrlValue(statusResponse.url);
        setIsUrlLocked(true);
        setUploadProgress(100);
      } catch (error) {
        setUploadError(
          error instanceof Error ? error.message : 'Failed to upload image'
        );
      } finally {
        setIsUploading(false);
      }
    },
    [updateImageDimensionsFromSource]
  );

  const handleFileChange = React.useCallback<
    NonNullable<React.ComponentPropsWithoutRef<'input'>['onChange']>
  >(
    async (event) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      await uploadImageFile(file);
      event.target.value = '';
    },
    [uploadImageFile]
  );

  const insert = React.useCallback(() => {
    const src = urlValue.trim();

    if (!src) {
      return;
    }

    const parsedWidth = Number(widthValue.trim());
    const parsedHeight = Number(heightValue.trim());

    editor.update(() => {
      if (savedSelectionRef.current) {
        $setSelection(savedSelectionRef.current);
      }
    });

    editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
      height:
        Number.isFinite(parsedHeight) && parsedHeight > 0
          ? parsedHeight
          : 'auto',
      src,
      width:
        Number.isFinite(parsedWidth) && parsedWidth > 0
          ? parsedWidth
          : 'inherit',
    });

    closeEditor();
  }, [closeEditor, editor, heightValue, urlValue, widthValue]);

  return {
    closeEditor,
    fileInputRef,
    handleFileChange,
    handleHeightChange,
    handleWidthChange,
    heightValue,
    inputRef,
    insert,
    isEditorVisible,
    isUploading,
    isUrlLocked,
    openEditor,
    setIsUrlLocked,
    setUploadError,
    setUploadProgress,
    setUrlValue,
    uploadError,
    uploadProgress,
    urlValue,
    widthValue,
  };
}
